import { stringify } from 'query-string';

import { LABEL_OPTIONS_VALUE_ALIAS_MAPPING, RETURN_TRANSPORT_PLAN_NAME } from 'constants/returnLabelOptions';
import { PROVIDE_LABEL_OPTIONS } from 'constants/exchanges';
import type { Order } from 'types/mafia';
import type { ShippingAddress } from 'types/balder';
import type { AMPLITUDE_RETURNS_STEP_NAME } from 'constants/amplitude';
import { AMPLITUDE_RETURN_STEP, AMPLITUDE_RETURN_TYPE } from 'constants/amplitude';
import type { ReturnsBoxInfo } from 'types/returns';
import type { ReturnStepEventData } from 'types/amplitude';

function extractParams(paramString: string) {
  // Two regexps instead of one since we don't know the order of the params in the query string.
  if (paramString) {
    const orderIdExtractor = /[?&]{1}purchaseId=(\d*)/;
    const productsExtractor = /[?&]{1}lineItemId=(\d*)/;
    const orderId = (paramString.match(orderIdExtractor) || [])[1] || '';
    const products = (paramString.match(productsExtractor) || [])[1] || '';
    return { orderId, products };
  }

  return {};
}

function getDaysRemaining(orderDate?: string, nowTime = new Date().getTime()) {
  if (!orderDate) {
    return 0;
  }

  const formattedDate = new Date(orderDate.split(' at ').join(' '));
  const expireTime = formattedDate.setFullYear(formattedDate.getFullYear() + 1);
  return Math.floor((new Date(expireTime - formattedDate.getMilliseconds()).getTime() - nowTime) / 86400000); // milliseconds in a day
}

function latestReturnTypes(order: Order) {
  const { lineItems = [] } = order || {};
  const mostRecentReturn = lineItems.reduce((mostRecentReturnLineItem, lineItem) => {
    const { returnDetails: { creationDate = 0 } = {} } = lineItem;
    return !mostRecentReturnLineItem?.returnDetails || creationDate > (mostRecentReturnLineItem?.returnDetails?.creationDate || 0)
      ? lineItem
      : mostRecentReturnLineItem;
  }, lineItems[0]);

  // Fallback to print since we don't have proper any carrier instruction verbiage. Sherman Siu is working on the next step decisions. Fix will come next.
  if (mostRecentReturn?.returnDetails?.planName === RETURN_TRANSPORT_PLAN_NAME.AnyCarrier) {
    return PROVIDE_LABEL_OPTIONS.PRINT;
  }

  return mostRecentReturn?.returnDetails?.planName
    ? LABEL_OPTIONS_VALUE_ALIAS_MAPPING[mostRecentReturn.returnDetails.planName as keyof typeof LABEL_OPTIONS_VALUE_ALIAS_MAPPING]
    : undefined;
}

function formatAddress(address: ShippingAddress) {
  const { addressId, name, addressLine1, addressLine2, city, state, postalCode } = address;

  return {
    addressId: addressId,
    addressLine1: addressLine1,
    addressLine2: addressLine2,
    city: city,
    fullName: name,
    postalCode: postalCode,
    stateOrRegion: state
  };
}

const getCancelReturnUrl = (orderId: string, unitIds: string[], isUPSPickup: boolean, prependSlash: boolean = false, isMyAccRedesign: boolean) =>
  orderId && unitIds?.length > 0
    ? `${prependSlash ? '/' : ''}${isMyAccRedesign ? 'my-return/cancel' : 'return/cancel'}?id=${orderId}&ups=${!!isUPSPickup}&${stringify(
        { items: unitIds },
        { arrayFormat: 'bracket' }
      )}`
    : null;

const isReturnWithQRCode = (planName: string) => {
  const methodsWithQRCode = [
    RETURN_TRANSPORT_PLAN_NAME.LabelLess_UPSZapposDropOff,
    RETURN_TRANSPORT_PLAN_NAME.PackageLess_UPSZapposDropOff,
    RETURN_TRANSPORT_PLAN_NAME.PackageLess_WFMZapposDropOff
  ];

  return methodsWithQRCode.some(method => method === planName);
};

const extractShipmentLineItemIdFromUnitId = (str: string) => {
  if (!str) {
    return '';
  }
  const parts = str.split('#');
  if (parts.length < 2) {
    return '';
  }
  parts.pop();
  const secondLastPart = parts.pop();
  const isNumber = /^\d+$/.test(secondLastPart ?? '');

  return isNumber ? secondLastPart : '';
};
const zeroAmounts = {
  '$0.00': true,
  '$0': true,
  '0': true,
  '-': true,
  '': true
};

const isZeroAmount = (amount: string): boolean => amount === null || amount === undefined || amount in zeroAmounts;

const isSubmitDisabled = (conditions: boolean[]): boolean => conditions.some(condition => condition === true);

const buildReturnStepAmplitudeEvent = ({
  boxInfo,
  refundTotal,
  contractIds,
  returnMethod,
  refundMethod,
  returnStepName,
  sessionId,
  userInfo
}: {
  boxInfo: ReturnsBoxInfo;
  refundTotal: number;
  contractIds: string[];
  returnMethod: string;
  refundMethod: string;
  sessionId: string;
  returnStepName: (typeof AMPLITUDE_RETURNS_STEP_NAME)[keyof typeof AMPLITUDE_RETURNS_STEP_NAME];
  userInfo: ReturnStepEventData['user'];
}): ReturnStepEventData => {
  const allItems = Object.values(boxInfo).flatMap(box => box.items);
  const returnIds = contractIds.join(','); // we dont seem to have this.. set it to contract ids

  return {
    event: AMPLITUDE_RETURN_STEP,
    sessionId,
    screen: {
      type: AMPLITUDE_RETURN_TYPE,
      name: returnStepName
    },
    user: userInfo,
    return: {
      ...(returnIds ? { return_id: returnIds } : {}),
      ...(returnMethod ? { return_method: returnMethod } : {}),
      ...(refundMethod ? { refund_method: refundMethod } : {}),
      return_items: allItems.length,
      return_boxes: Object.keys(boxInfo).length,
      return_total: refundTotal,
      itemCount: allItems.length,
      items: allItems.map(item => {
        const returnReason = item?.returnReasons?.[item?.reasonCode as keyof typeof item.returnReasons];
        return {
          brandName: item.brand,
          name: item.title,
          price: item.ourPrice,
          productId: item.asin,
          qty: 1,
          styleId: item.asin,
          ...(returnReason ? { return_reason: returnReason } : {})
        };
      })
    }
  };
};

const ReturnsUtils = {
  extractParams,
  formatAddress,
  getDaysRemaining,
  latestReturnTypes,
  getCancelReturnUrl,
  isReturnWithQRCode,
  isSubmitDisabled,
  isZeroAmount,
  extractShipmentLineItemIdFromUnitId,
  buildReturnStepAmplitudeEvent
};

export default ReturnsUtils;
